import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Clock, Users } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import Analytics from '@components/analytics/Analytics';

const AnalyticsPage = () => {
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Analytics - RDX Ticketing System';
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline style objects to match Dashboard visual language
  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    hero: {
      borderRadius: '1rem',
      padding: '1.25rem',
      color: 'white',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(2,6,23,0.08)'
    },
    heroInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    heroLeft: {
      flex: 1,
      minWidth: 0
    },
    heroTitle: {
      fontSize: '1.375rem',
      fontWeight: 700,
      margin: 0,
      color: 'white',
      lineHeight: 1.1
    },
    heroSub: {
      marginTop: '0.375rem',
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    heroRight: {
      textAlign: 'right',
      minWidth: '11rem'
    },
    dateText: {
      margin: 0,
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    metaText: {
      marginTop: '0.25rem',
      fontSize: '0.8125rem',
      color: 'rgba(255,255,255,0.85)',
      textTransform: 'capitalize'
    },
    cardWrapper: {
      backgroundColor: 'transparent'
    }
  };

  // gradient background for analytics (purple theme)
  const heroGradient = {
    background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)'
  };

  return (
    <motion.div
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Analytics Hero */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.hero, ...heroGradient }}>
          <div style={styles.heroInner}>
            <div style={styles.heroLeft}>
              <h1 style={styles.heroTitle}>
                <BarChart3 style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }} />
                Analytics Dashboard
              </h1>
              <p style={styles.heroSub}>
                Insights and performance metrics for your ticketing system.
              </p>
            </div>

            <div style={styles.heroRight} className="hidden-md">
              <p style={styles.dateText}>
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p style={styles.metaText}>
                {user?.role ? `${user.role}` : '—'} {user?.department ? `• ${user.department}` : ''}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Analytics Content */}
      <motion.div variants={itemVariants}>
        <div style={styles.cardWrapper}>
          <Analytics />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AnalyticsPage;